import React, { useEffect, useState, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Define the shape of our user object
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}
// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
}
// Default credentials
const DEFAULT_CREDENTIALS = {
  email: 'admin@riders.com',
  password: 'password123'
};
// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => false,
  logout: () => {},
  error: null
});
// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Check if user is already logged in
  useEffect(() => {
    const checkAuthStatus = () => {
      const storedUser = localStorage.getItem('riders_user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem('riders_user');
        }
      }
      setIsLoading(false);
    };
    checkAuthStatus();
  }, []);
  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!isLoading && user && location.pathname === '/signin') {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate, location]);
  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    setError(null);
    // In a real app, this would be an API call
    // For now, we'll just check against our default credentials
    if (email === DEFAULT_CREDENTIALS.email && password === DEFAULT_CREDENTIALS.password) {
      const userData: User = {
        id: '1',
        name: 'Admin User',
        email: DEFAULT_CREDENTIALS.email,
        role: 'admin',
        avatar: ''
      };
      setUser(userData);
      localStorage.setItem('riders_user', JSON.stringify(userData));
      return true;
    } else {
      setError('Invalid email or password');
      return false;
    }
  };
  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('riders_user');
    navigate('/signin');
  };
  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    error
  };
  return <AuthContext.Provider value={value} data-id="element-780">{children}</AuthContext.Provider>;
};